import type { Nullable } from '@silverhand/essentials';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';

import ConnectIcon from '@/assets/icons/connect-icon.svg?react';
import DynamicT from '@/shared/components/DynamicT';
import { isIdentityConsole } from '@/utils/cookies';

import MellowMark from '../../../../toolkit/core-kit/assets/mellow-mark.svg?react';

import styles from './index.module.scss';

export type Props = {
  readonly className?: string;
  readonly logo?: Nullable<string>;
  readonly thirdPartyLogo?: Nullable<string>;
  readonly headline?: TFuncKey;
  readonly headlineInterpolation?: Record<string, unknown>;
};

const logtoStockLogoPattern =
  /(^https?:\/\/(?:www\.)?logto\.io\/logo(?:-dark)?\.svg$)|(^\/logo(?:-dark)?\.svg$)/iu;

const isLogtoStockLogo = (logo?: Nullable<string>) =>
  Boolean(logo && logtoStockLogoPattern.test(logo));

const CastIdentityBrand = () => (
  <div className={styles.castBrand} aria-label="Cast Identity">
    <svg
      aria-hidden
      className={styles.castGlyph}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 1.5L12.4 9.6L20.5 11L12.4 12.4L11 20.5L9.6 12.4L1.5 11L9.6 9.6L11 1.5Z"
        fill="currentColor"
      />
    </svg>
    <span>Cast Identity</span>
  </div>
);

const BrandingHeader = ({
  logo,
  thirdPartyLogo,
  headline,
  headlineInterpolation,
  className,
}: Props) => {
  const shouldUseCastBrand = !logo || isLogtoStockLogo(logo);
  const shouldShowLogo = Boolean(thirdPartyLogo ?? logo ?? shouldUseCastBrand);
  const shouldConnectSvg = Boolean(thirdPartyLogo) && (Boolean(logo) || shouldUseCastBrand);

  return (
    <div className={classNames(styles.container, className)}>
      {shouldShowLogo && (
        <div className={styles.logoWrapper}>
          {thirdPartyLogo && (
            <img className={styles.logo} alt="third party logo" src={thirdPartyLogo} />
          )}
          {shouldConnectSvg && <ConnectIcon className={styles.connectIcon} />}
          {shouldUseCastBrand ? (
            isIdentityConsole ? (
              <div className={styles.castBrand}>
                <MellowMark aria-hidden className={styles.mellowGlyph} />
                <span>Identity Management</span>
              </div>
            ) : (
              <CastIdentityBrand />
            )
          ) : (
            <img className={styles.logo} alt="app logo" src={logo} />
          )}
        </div>
      )}

      {headline && (
        <div className={styles.headline}>
          <DynamicT forKey={headline} interpolation={headlineInterpolation} />
        </div>
      )}
    </div>
  );
};

export default BrandingHeader;
